import { db } from '@/db/db';
import { put, tombstone } from '@/db/repo';
import { nowMs } from '@/db/clock';
import { DEFAULT_COURSE } from '@/constants/app';
import type { LibraryFile } from '@/domain/types';
import { prepareFile, RefusedFile } from './prepare';
import { deleteRemoteFile, downloadFile, uploadFile, type TransferProgress } from './transfer';
import { blobToBytes, bytesToBlob } from './bytes';

/**
 * The Library: one general collection of PDFs and images, with no attachment to
 * any class. File metadata syncs like every other record; the bytes travel
 * separately in chunks.
 *
 * Only pinned files keep their bytes on this device. A file added here is
 * pinned automatically — the bytes are already on the device, and throwing them
 * away only to download them again would be perverse.
 */

export class NotAvailableOffline extends Error {
  constructor() {
    super('Not available offline');
  }
}

export { RefusedFile };

function newFileId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
}

export async function listFiles(): Promise<LibraryFile[]> {
  const rows = await db.files.toArray();
  return rows.filter((f) => !f.deleted).sort((a, b) => b.addedAt - a.addedAt);
}

export async function isPinned(fileId: string): Promise<boolean> {
  return Boolean(await db.pins.get(fileId));
}

export async function pinnedIds(): Promise<Set<string>> {
  return new Set((await db.pins.toArray()).map((p) => p.fileId));
}

export interface AddResult {
  file: LibraryFile;
  shrunkFrom?: number;
}

/** Validates, shrinks an image, stores the bytes here and records the file. */
export async function addFile(input: File): Promise<AddResult> {
  const prepared = await prepareFile(input);
  const id = newFileId();

  const file = await put<LibraryFile>('files', {
    id,
    deleted: false,
    course: DEFAULT_COURSE,
    name: prepared.name,
    mime: prepared.mime,
    size: prepared.blob.size,
    storageKey: id,
    uploaded: false,
    addedAt: nowMs(),
    lastPage: 1,
  });

  await db.blobs.put({
    fileId: id,
    data: await blobToBytes(prepared.blob),
    mime: prepared.mime,
    cachedAt: nowMs(),
    size: prepared.blob.size,
  });
  await db.pins.put({ fileId: id, pinnedAt: nowMs() });

  return { file, ...(prepared.originalSize ? { shrunkFrom: prepared.originalSize } : {}) };
}

/** Pushes the bytes up and marks the record uploaded. Safe to call again. */
export async function uploadPending(
  fileId: string,
  onProgress?: (p: TransferProgress) => void,
): Promise<void> {
  const record = await db.files.get(fileId);
  if (!record || record.deleted) return;
  const cached = await db.blobs.get(fileId);
  if (!cached) throw new Error('The bytes for this file are not on this device.');

  await uploadFile(record.storageKey, bytesToBlob(cached.data, cached.mime), onProgress);
  await put<LibraryFile>('files', { ...record, uploaded: true });
}

/** Uploads anything that never finished. Called when the app opens. */
export async function uploadAllPending(): Promise<number> {
  const pending = (await listFiles()).filter((f) => !f.uploaded);
  let done = 0;
  for (const file of pending) {
    const cached = await db.blobs.get(file.id);
    if (!cached) continue;
    try {
      await uploadPending(file.id);
      done += 1;
    } catch {
      // Left for the next attempt; the file keeps showing as not uploaded.
    }
  }
  return done;
}

/**
 * The bytes, from this device if they are here, otherwise from the server.
 * Unpinned files are simply unavailable with no connection.
 */
export async function getFileBlob(
  fileId: string,
  onProgress?: (p: TransferProgress) => void,
): Promise<Blob> {
  const cached = await db.blobs.get(fileId);
  if (cached) return bytesToBlob(cached.data, cached.mime);

  const record = await db.files.get(fileId);
  if (!record || record.deleted) throw new Error('That file is no longer in your Library.');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new NotAvailableOffline();

  const blob = await downloadFile(record.storageKey, record.size, record.mime, onProgress);

  // Only keep it if it is pinned; otherwise it was a one-off view.
  if (await isPinned(fileId)) {
    await db.blobs.put({
      fileId,
      data: await blobToBytes(blob),
      mime: record.mime,
      cachedAt: nowMs(),
      size: blob.size,
    });
  }
  return blob;
}

export async function renameFile(fileId: string, name: string): Promise<void> {
  const record = await db.files.get(fileId);
  if (!record || record.deleted) return;
  const trimmed = name.trim();
  if (!trimmed) return;
  await put<LibraryFile>('files', { ...record, name: trimmed });
}

export async function setLastPage(fileId: string, page: number): Promise<void> {
  const record = await db.files.get(fileId);
  if (!record || record.deleted || record.lastPage === page) return;
  await put<LibraryFile>('files', { ...record, lastPage: page });
}

export async function setPageCount(fileId: string, pageCount: number): Promise<void> {
  const record = await db.files.get(fileId);
  if (!record || record.deleted || record.pageCount === pageCount) return;
  await put<LibraryFile>('files', { ...record, pageCount });
}

/** Tombstones the record, drops the bytes here and removes them from the server. */
export async function deleteFile(fileId: string): Promise<void> {
  const record = await db.files.get(fileId);
  await db.blobs.delete(fileId);
  await db.pins.delete(fileId);
  await tombstone('files', fileId);

  if (record && record.uploaded) {
    try {
      await deleteRemoteFile(record.storageKey);
    } catch {
      // The record is already a tombstone, so the file is gone from the app.
      // Orphaned bytes are cleaned up by the next successful delete attempt.
    }
  }
}

export async function setPinned(fileId: string, pinned: boolean): Promise<void> {
  if (!pinned) {
    await db.pins.delete(fileId);
    await db.blobs.delete(fileId);
    return;
  }
  // Pinning means it has to be here, so fetch it if it is not.
  if (!(await db.blobs.get(fileId))) {
    const record = await db.files.get(fileId);
    if (!record || record.deleted) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new NotAvailableOffline();
    const blob = await downloadFile(record.storageKey, record.size, record.mime);
    await db.blobs.put({
      fileId,
      data: await blobToBytes(blob),
      mime: record.mime,
      cachedAt: nowMs(),
      size: blob.size,
    });
  }
  await db.pins.put({ fileId, pinnedAt: nowMs() });
}

export async function pinnedBytes(): Promise<number> {
  const rows = await db.blobs.toArray();
  return rows.reduce((total, row) => total + row.size, 0);
}

export async function unpinAll(): Promise<number> {
  const rows = await db.blobs.toArray();
  const freed = rows.reduce((total, row) => total + row.size, 0);
  await db.blobs.clear();
  await db.pins.clear();
  return freed;
}

export function searchFiles(files: LibraryFile[], query: string): LibraryFile[] {
  const q = query.trim().toLowerCase();
  if (!q) return files;
  return files.filter((f) => f.name.toLowerCase().includes(q));
}
